import { useState, useEffect } from 'react';
import { useView } from '../contexts/ViewContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Box,
  Search,
  Download,
  Upload,
  Pencil,
  Edit3,
  History,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { suppliersAPI, type SupplierStockLevel } from '../lib/api';
import { useIsSupplier } from '../hooks/usePortalMode';
import { useFormatters } from '../contexts/PreferencesContext';
import { Pagination, PAGE_SIZE_OPTIONS } from '../components/ui/pagination';
import { RowsPerPageSelect } from '../components/ui/rows-per-page';
import StockUploadDialog from '../components/suppliers/StockUploadDialog';
import ProposePartChangeModal from '../components/call-off/stock-planning/ProposePartChangeModal';
import PartChangeHistoryDrawer from '../components/call-off/stock-planning/PartChangeHistoryDrawer';
import StockCorrectionDialog from '../components/call-off/stock-upload/StockCorrectionDialog';
import StockInputHistoryDrawer from '../components/call-off/stock-upload/StockInputHistoryDrawer';
import { notifyExportResult, type ExportTruncation } from '../lib/exportTruncation';
import ExportTruncationBanner from '../components/export/ExportTruncationBanner';

export default function StockLevelsPage() {
  const isSupplier = useIsSupplier();
  const { getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [stockLevels, setStockLevels] = useState<SupplierStockLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  // CR-7432: counts from the last export, when it was cut short by the row cap.
  const [truncation, setTruncation] = useState<ExportTruncation | null>(null);
  const [proposeTarget, setProposeTarget] = useState<SupplierStockLevel | null>(null);
  const [historyTarget, setHistoryTarget] = useState<SupplierStockLevel | null>(null);
  const [correctTarget, setCorrectTarget] = useState<SupplierStockLevel | null>(null);
  const [inputHistoryTarget, setInputHistoryTarget] = useState<SupplierStockLevel | null>(null);
  const [pageSize, setPageSize] = useState(25);
  // True when the current page has at least one row whose (supplier, customer)
  // relationship is NOT call-off-enabled. Controls Template/Upload CSV button
  // visibility — those buffer-fields aren't usable for call-off suppliers.
  const hasEditableRows = isSupplier && stockLevels.some(s => !s.call_off_enabled);
  // The Actions column itself is shown for any supplier view, because the
  // "Propose qty change" entry point applies regardless of call-off setup.
  const showActionsColumn = isSupplier;

  useEffect(() => {
    if (viewContextLoading) return;
    fetchStockLevels();
  }, [viewContextLoading, currentPage, pageSize, searchTerm, isSupplier, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // CR-7432: the banner names a count for the cohort it was exported from.
  // Once the search or the scope moves, those numbers describe a file nobody
  // is looking at any more.
  useEffect(() => {
    setTruncation(null);
  }, [searchTerm, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  const fetchStockLevels = async () => {
    try {
      setLoading(true);
      const viewParams = getViewQueryParams();
      const response = await suppliersAPI.getStockLevels({
        search: searchTerm || undefined,
        page: currentPage,
        page_size: pageSize,
        ordering: 'part_number',
        ...viewParams,
      });
      setStockLevels(response.data.results);
      setTotalCount(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));
    } catch (err) {
      console.error('Error fetching stock levels:', err);
      toast.error('Failed to load stock levels');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await suppliersAPI.downloadStockTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'stock_levels_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      toast.error('Failed to download template');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await suppliersAPI.exportStockLevels({
        part_number: searchTerm || undefined,
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_levels_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      // CR-7432: "Stock levels exported" was said whether or not the 10,000-row
      // cap had cut the file short — the worst case for a supplier doing a
      // physical stock check against it.
      setTruncation(notifyExportResult(response, 'Stock levels exported'));
    } catch (err) {
      console.error('Error exporting:', err);
      toast.error('Failed to export stock levels');
    } finally {
      setExporting(false);
    }
  };

  const { formatDateTime: contextFormatDateTime } = useFormatters();

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return contextFormatDateTime(dateStr);
  };

  if (loading && stockLevels.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Box className="h-5 w-5 text-primary" />
              <CardTitle>Stock Levels</CardTitle>
              <Badge variant="outline">{totalCount} parts</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchStockLevels}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {hasEditableRows && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadTemplate}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Template
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUploadDialog(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload CSV
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 empty:mb-0">
            <ExportTruncationBanner
              truncation={truncation}
              onDismiss={() => setTruncation(null)}
            />
          </div>
          {/* Search Bar */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by part number..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {/* CR-8186: the same choices as the Rows control at the bottom,
                so a size picked in either one shows in both. */}
            <RowsPerPageSelect
              value={pageSize}
              onChange={(n) => { setPageSize(n); setCurrentPage(1); }}
              options={PAGE_SIZE_OPTIONS}
            />
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table wrapperClassName="max-h-[calc(100vh-340px)]">
              <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background [&_th]:shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
                <TableRow>
                  <TableHead>Part Number</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Qty Available</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Updated By</TableHead>
                  <TableHead>Notes</TableHead>
                  {showActionsColumn && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {stockLevels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showActionsColumn ? 9 : 8} className="text-center py-8 text-muted-foreground">
                      No stock levels found. {hasEditableRows && 'Upload a CSV to add stock data.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  stockLevels.map((stock) => (
                    <TableRow key={stock.id}>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => setInputHistoryTarget(stock)}
                          className="text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
                          title="View input history for this part"
                        >
                          {stock.part_number}
                        </button>
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate" title={stock.description || ''}>
                        <span className="text-sm">{stock.description || '-'}</span>
                      </TableCell>
                      <TableCell>{stock.customer_name}</TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono">{stock.quantity_available.toLocaleString()}</span>
                      </TableCell>
                      <TableCell>{stock.unit_of_measure}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(stock.last_updated)}
                      </TableCell>
                      <TableCell className="text-sm">{stock.updated_by_name || '-'}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        <span className="text-sm text-muted-foreground">{stock.notes || '-'}</span>
                      </TableCell>
                      {showActionsColumn && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setProposeTarget(stock)}
                              title="Propose PO qty change for this part"
                            >
                              <Edit3 className="h-4 w-4 mr-1" />
                              <span className="text-xs">Propose</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setHistoryTarget(stock)}
                              title="View part change history"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            {!stock.call_off_enabled && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCorrectTarget(stock)}
                                title="Correct stock qty (requires reason)"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                itemsPerPage={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(n) => { setPageSize(n); setCurrentPage(1); }}
                itemLabel="stock levels"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <StockUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onUploadComplete={() => {
          setShowUploadDialog(false);
          fetchStockLevels();
        }}
      />

      {/* Propose part-level PO qty change */}
      {proposeTarget && (
        <ProposePartChangeModal
          open={!!proposeTarget}
          onOpenChange={(open) => { if (!open) setProposeTarget(null); }}
          partNumber={proposeTarget.part_number}
          customerId={proposeTarget.customer}
          onSubmitted={fetchStockLevels}
        />
      )}

      {/* Part-level change history */}
      {historyTarget && (
        <PartChangeHistoryDrawer
          open={!!historyTarget}
          onOpenChange={(open) => { if (!open) setHistoryTarget(null); }}
          partNumber={historyTarget.part_number}
          customerId={historyTarget.customer}
        />
      )}

      {/* Mandatory-reason stock correction (replaces inline Pencil edit) */}
      {correctTarget && (
        <StockCorrectionDialog
          open={!!correctTarget}
          onOpenChange={(open) => { if (!open) setCorrectTarget(null); }}
          partNumber={correctTarget.part_number}
          description={correctTarget.description || ''}
          currentQty={correctTarget.quantity_available}
          unitOfMeasure={correctTarget.unit_of_measure}
          customerId={correctTarget.customer}
          onCorrected={() => { setCorrectTarget(null); fetchStockLevels(); }}
        />
      )}

      {/* Per-part stock input history (click on the part number) */}
      {inputHistoryTarget && (
        <StockInputHistoryDrawer
          open={!!inputHistoryTarget}
          onOpenChange={(open) => { if (!open) setInputHistoryTarget(null); }}
          partNumber={inputHistoryTarget.part_number}
          description={inputHistoryTarget.description || ''}
        />
      )}
    </div>
  );
}
